import React, { useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Button, Tooltip } from 'antd';
import {
  PlusOutlined,
  TeamOutlined,
  CaretRightOutlined,
  ReloadOutlined,
  RocketOutlined,
} from '@ant-design/icons';

import {
  selectors,
  clearSessionReplayDefaults,
  fetchSessionReplayDefaults,
  SCENARIO_TYPES,
  RISK_APPROACHES,
  Session,
} from 'state';
import { formatters } from 'services';

import SessionSettings from './SessionSettings';
import CreateSessionStudio from './create_session_v2/CreateSessionStudio';
import { useClassicCreateModal } from './create_session_v2/useClassicCreateModal';
import { useScenarioLaunchAccess } from './useScenarioLaunchAccess';
import LaunchScenarioModal from './LaunchScenarioModal';
import KlassUsers from './KlassUsers';
import {
  byNewest,
  classCode,
  isFinished,
  statusMeta,
  timingLine,
} from './helpers';

type Props = {
  klassId: number;
};

/**
 * ACTION POLICY (spec §7): this dashboard exposes ONLY Join/Setup/Review and Replay.
 * Inline Pause / Resume / End are intentionally absent — to alter a session the
 * trainer must Join it first. Green Join is reserved for LIVE (running)
 * sessions; Paused/Delay/Frozen use a neutral Join, Creating uses a neutral
 * Setup, and Finished uses a neutral Review. Every row offers Join/Setup/Review
 * (left) then a Replay icon-button (right, on all statuses).
 */
const SessionRow: React.FC<{
  session: Session;
  replayLoading: boolean;
  onReplay: (id: number) => void;
}> = ({ session, replayLoading, onReplay }) => {
  const meta = statusMeta(session.status);
  const rowClass = meta.isRunning
    ? 'is-running'
    : meta.isPaused
      ? 'is-paused'
      : '';
  const pillClass = meta.isRunning
    ? 'td-pill--running'
    : meta.isPaused
      ? 'td-pill--paused'
      : meta.isFinished
        ? 'td-pill--finished'
        : meta.isCreating
          ? 'td-pill--creating'
          : 'td-pill--neutral';

  const join = (
    <Link
      // A still-creating session isn't set up yet — open it straight on the
      // Session Settings tab; everything else lands on Overview.
      to={
        meta.isCreating
          ? `/session/${session.id}/session-settings`
          : `/session/${session.id}`
      }
      target="_blank"
      rel="noopener noreferrer"
    >
      <Button
        size="small"
        icon={<CaretRightOutlined />}
        className={meta.isRunning ? 'td-btn--live' : undefined}
      >
        {meta.isFinished ? 'Review' : meta.isCreating ? 'Setup' : 'Join'}
      </Button>
    </Link>
  );

  // Icon-only Replay, available on every session; hover explains it.
  const replay = (
    <Tooltip title={session.execution_engine === 'fast' ? 'Replay is unavailable for native sessions' : 'Replay this session'}>
      <Button
        size="small"
        aria-label="Replay this session"
        icon={<ReloadOutlined />}
        loading={replayLoading}
        // Native replay is outside this milestone; the backend also refuses forged requests.
        disabled={session.execution_engine === 'fast'}
        onClick={() => onReplay(session.id)}
      />
    </Tooltip>
  );

  return (
    <div className={`td-srow ${rowClass}`}>
      <div className="td-sid">#{session.id}</div>
      <div className="td-sfacts">
        <div className="td-line1">
          <span className={`td-pill ${pillClass}`}>
            {meta.isRunning && <span className="td-dot" />}
            {timingLine(session)}
          </span>
          <span className="td-kv">
            <b>{SCENARIO_TYPES[session.scenario_type]}</b>
          </span>
          <span className="td-kv">
            · {RISK_APPROACHES[session.risk_approach]}
          </span>
        </div>
        <div className="td-kv">
          Created{' '}
          {session.create_date
            ? formatters.dateWithTime(session.create_date)
            : '—'}
          {session.finish_date
            ? ` · ended ${formatters.dateWithTime(session.finish_date)}`
            : ''}
        </div>
      </div>
      <div className="td-sactions">
        {join}
        {replay}
      </div>
    </div>
  );
};

const SessionsPanel: React.FC<Props> = ({ klassId }) => {
  const dispatch = useDispatch();
  const allSessions = useSelector(selectors.sessions, shallowEqual);
  const klass = useSelector(selectors.klassById(klassId));
  const replayDefaults = useSelector(selectors.replaySessionDefaults);
  const replayDefaultsFetched = useSelector(
    selectors.replaySessionDefaultsFetched,
  );
  const replayDefaultsWaiting = useSelector(
    selectors.replaySessionDefaultsWaiting,
  );
  const replaySourceId = useSelector(selectors.replaySessionSourceId);

  const [createVisible, setCreateVisible] = useState(false);
  const [membersVisible, setMembersVisible] = useState(false);
  const [launchVisible, setLaunchVisible] = useState(false);
  const [pendingReplayId, setPendingReplayId] = useState<number | null>(null);
  // The redesigned create_session_v2 studio is the default. Classic is an
  // admin break-glass fall-back, armed only via `?classicCreate=1` in the URL.
  const useClassic = useClassicCreateModal();
  // Server-side permission, managed by an admin on the Trainers screen and on
  // by default for a trainer who runs a class with trainees (see
  // useScenarioLaunchAccess). The blueprint launch endpoint enforces its own
  // permissions; this only decides whether the button is drawn.
  const scenarioLaunchEnabled = useScenarioLaunchAccess();

  const sessions = useMemo(
    () => allSessions.filter(s => s.klass === klassId),
    [allSessions, klassId],
  );
  const active = useMemo(
    () => sessions.filter(s => !isFinished(s)).sort(byNewest),
    [sessions],
  );
  const finished = useMemo(
    () => sessions.filter(isFinished).sort(byNewest),
    [sessions],
  );

  // Replay reuses the Create modal: fetch defaults for the source session, then
  // open the modal once they arrive (same two-step flow as the old dashboard).
  useEffect(() => {
    if (
      pendingReplayId !== null &&
      replayDefaultsFetched &&
      replaySourceId === pendingReplayId
    ) {
      setCreateVisible(true);
      setPendingReplayId(null);
    }
  }, [pendingReplayId, replayDefaultsFetched, replaySourceId]);

  const onReplay = (id: number) => {
    setPendingReplayId(id);
    dispatch(fetchSessionReplayDefaults({ id }));
  };

  const openCreate = () => {
    setPendingReplayId(null);
    dispatch(clearSessionReplayDefaults({}));
    setCreateVisible(true);
  };

  const closeCreate = () => {
    setCreateVisible(false);
    setPendingReplayId(null);
    dispatch(clearSessionReplayDefaults({}));
  };

  if (!klassId || !klass) {
    return (
      <section className="td-panel">
        <div className="td-empty">
          <h4>No class selected</h4>
          <p>Pick a class from the list to see its sessions.</p>
        </div>
      </section>
    );
  }

  const code = classCode(klass);
  const created = klass.start_date
    ? formatters.dateWithTime(klass.start_date as string)
    : '—';
  const replayLoadingFor = (id: number) =>
    replayDefaultsWaiting && replaySourceId === id;

  return (
    <section className="td-panel td-fade">
      <div className="td-panel-head">
        <div style={{ minWidth: 0 }}>
          <div className="td-eyebrow">Sessions</div>
          <div className="td-title">{code}</div>
          <div className="td-sub td-meta">
            Created {created} · {sessions.length} session
            {sessions.length !== 1 ? 's' : ''}
          </div>
        </div>
        <div className="td-panel-actions">
          <Button
            icon={<TeamOutlined />}
            onClick={() => setMembersVisible(true)}
          >
            Members
          </Button>
          {/* Create lives in the header only when the class already has
              sessions; the empty-class state below owns the sole Create CTA. */}
          {sessions.length > 0 && (
            <Button icon={<PlusOutlined />} onClick={openCreate}>
              Create session
            </Button>
          )}
          {/* Launch from Scenario — shown to trainers an admin has allowed. */}
          {scenarioLaunchEnabled && (
            <Button
              icon={<RocketOutlined />}
              onClick={() => setLaunchVisible(true)}
            >
              Launch from Scenario
            </Button>
          )}
        </div>
      </div>

      {sessions.length === 0 ? (
        <div className="td-empty">
          <div className="td-ic">
            <PlusOutlined />
          </div>
          <h4>No sessions in this class yet</h4>
          <p>
            Create the first session to start training{' '}
            {code.split('_')[0] || 'this class'}.
          </p>
          <Button icon={<PlusOutlined />} onClick={openCreate}>
            Create session
          </Button>
        </div>
      ) : (
        <div className="td-panel-body">
          {active.length > 0 && (
            <>
              <div className="td-grouplabel">
                <span className="td-eyebrow">Active</span>
                <span className="td-rule" />
              </div>
              {active.map(s => (
                <SessionRow
                  key={s.id}
                  session={s}
                  replayLoading={replayLoadingFor(s.id)}
                  onReplay={onReplay}
                />
              ))}
            </>
          )}
          {finished.length > 0 && (
            <>
              <div className="td-grouplabel">
                <span className="td-eyebrow">Finished sessions</span>
                <span className="td-rule" />
              </div>
              {finished.map(s => (
                <SessionRow
                  key={s.id}
                  session={s}
                  replayLoading={replayLoadingFor(s.id)}
                  onReplay={onReplay}
                />
              ))}
            </>
          )}
        </div>
      )}

      {useClassic ? (
        <SessionSettings
          visible={createVisible}
          hide={closeCreate}
          klassId={klassId}
          replayDefaults={replayDefaults}
          replaySourceSessionId={replayDefaults ? replaySourceId : undefined}
        />
      ) : (
        <CreateSessionStudio
          visible={createVisible}
          hide={closeCreate}
          klassId={klassId}
          replayDefaults={replayDefaults}
          replaySourceSessionId={replayDefaults ? replaySourceId : undefined}
        />
      )}
      {membersVisible && (
        <KlassUsers
          visible={membersVisible}
          hide={() => setMembersVisible(false)}
          klassId={klassId}
        />
      )}
      {scenarioLaunchEnabled && launchVisible && (
        <LaunchScenarioModal
          visible={launchVisible}
          hide={() => setLaunchVisible(false)}
          klassId={klassId}
        />
      )}
    </section>
  );
};

export default SessionsPanel;
